<template>
  <div :class="ns.b()">
    <z-tooltip :content="scrollLeftLabel" placement="bottom">
      <span :class="ns.e('tool-wrap')">
        <button
          type="button"
          :class="[ns.e('tool'), ns.em('tool', 'first')]"
          :aria-label="scrollLeftLabel"
          :disabled="!canScrollLeft"
          @click="scrollTags(-1)"
        >
          <z-icon><d-arrow-left /></z-icon>
        </button>
      </span>
    </z-tooltip>

    <div :class="ns.e('scroll')" @wheel="onWheel">
      <z-scrollbar ref="scrollbarRef">
        <div :class="ns.e('list')" role="tablist">
          <z-dropdown
            v-for="tag in tags"
            :key="tag.key"
            trigger="contextmenu"
            :class="ns.e('dropdown')"
            @command="(command) => onDropdownCommand(command, tag)"
          >
            <div
              role="tab"
              :tabindex="0"
              :aria-selected="tag.key === activeKey"
              :aria-current="tag.key === activeKey ? 'page' : undefined"
              :class="[ns.e('item'), ns.is('active', tag.key === activeKey)]"
              @click="emit('select', tag)"
              @keydown.enter.prevent="emit('select', tag)"
              @keydown.space.prevent="emit('select', tag)"
              @auxclick="onAuxClick(tag, $event)"
              @mousedown="onItemMouseDown"
            >
              <span :class="ns.e('label')">
                <span v-if="tag.icon || $slots.icon" :class="ns.e('icon')">
                  <slot name="icon" :tag="tag">
                    <z-icon v-if="tag.icon">
                      <component :is="resolveIcon(tag.icon)" />
                    </z-icon>
                  </slot>
                </span>
                <span :class="ns.e('title')">{{ tag.title }}</span>
              </span>
              <button
                v-if="!tag.affix"
                type="button"
                :class="ns.e('close')"
                aria-label="关闭"
                @click.stop="emit('close', tag)"
              >
                <z-icon :size="10"><close /></z-icon>
              </button>
            </div>
            <template #dropdown>
              <z-dropdown-menu>
                <z-dropdown-item
                  command="reload"
                  :disabled="tag.key !== activeKey"
                >
                  {{ reloadLabel }}
                </z-dropdown-item>
                <z-dropdown-item command="close" :disabled="!!tag.affix">
                  {{ closeLabel }}
                </z-dropdown-item>
                <z-dropdown-item
                  divided
                  command="close-left"
                  :disabled="!canCloseLeft(tag)"
                >
                  {{ closeLeftLabel }}
                </z-dropdown-item>
                <z-dropdown-item
                  command="close-right"
                  :disabled="!canCloseRight(tag)"
                >
                  {{ closeRightLabel }}
                </z-dropdown-item>
                <z-dropdown-item
                  divided
                  command="close-others"
                  :disabled="!canCloseOthers(tag)"
                >
                  {{ closeOthersLabel }}
                </z-dropdown-item>
                <z-dropdown-item command="close-all" :disabled="!canCloseAll">
                  {{ closeAllLabel }}
                </z-dropdown-item>
              </z-dropdown-menu>
            </template>
          </z-dropdown>
        </div>
      </z-scrollbar>
    </div>

    <z-tooltip :content="scrollRightLabel" placement="bottom">
      <span :class="ns.e('tool-wrap')">
        <button
          type="button"
          :class="ns.e('tool')"
          :aria-label="scrollRightLabel"
          :disabled="!canScrollRight"
          @click="scrollTags(1)"
        >
          <z-icon><d-arrow-right /></z-icon>
        </button>
      </span>
    </z-tooltip>

    <slot name="extra" />

    <z-tooltip :content="allTagsLabel" placement="bottom">
      <span :class="ns.e('tool-wrap')">
        <z-dropdown
          trigger="click"
          :popper-class="ns.e('all-popper')"
          @command="handleSelectFromList"
        >
          <button
            type="button"
            :class="[ns.e('tool'), ns.em('tool', 'count')]"
            :aria-label="allTagsLabel"
          >
            <z-icon :size="14"><icon-menu /></z-icon>
            <span :class="ns.e('count')">{{ tags.length }}</span>
          </button>
          <template #dropdown>
            <z-dropdown-menu>
              <z-dropdown-item
                v-for="tag in tags"
                :key="tag.key"
                :command="tag.key"
                :class="{ 'is-active': tag.key === activeKey }"
              >
                <span :class="ns.e('all-item')">
                  <span v-if="tag.icon || $slots.icon" :class="ns.e('all-icon')">
                    <slot name="icon" :tag="tag">
                      <z-icon v-if="tag.icon">
                        <component :is="resolveIcon(tag.icon)" />
                      </z-icon>
                    </slot>
                  </span>
                  <span :class="ns.e('all-title')">{{ tag.title }}</span>
                </span>
              </z-dropdown-item>
            </z-dropdown-menu>
          </template>
        </z-dropdown>
      </span>
    </z-tooltip>

    <z-tooltip v-if="showReload" :content="reloadLabel" placement="bottom">
      <button
        type="button"
        :class="ns.e('tool')"
        :aria-label="reloadLabel"
        @click="emit('reload')"
      >
        <z-icon><refresh /></z-icon>
      </button>
    </z-tooltip>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Close, DArrowLeft, DArrowRight, Menu as IconMenu, Refresh } from '@zoryn/icons'
import { useNamespace } from '@zoryn/hooks'
import ZIcon from '@zoryn/components/icon'
import { ZDropdown, ZDropdownItem, ZDropdownMenu } from '@zoryn/components/dropdown'
import ZScrollbar from '@zoryn/components/scrollbar'
import ZTooltip from '@zoryn/components/tooltip'
import { resolveIcon } from './resolve-icon'
import type { AppTag, AppTagsCommand } from './types'
import {
  canCloseOtherTags,
  canCloseTagsLeft,
  canCloseTagsRight,
  hasClosableTags,
} from './tag-nav'

defineOptions({
  name: 'ZAppTagsBar',
})

const props = withDefaults(
  defineProps<{
    tags?: AppTag[]
    activeKey?: string
    showReload?: boolean
    reloadLabel?: string
    closeLabel?: string
    closeLeftLabel?: string
    closeRightLabel?: string
    closeOthersLabel?: string
    closeAllLabel?: string
    allTagsLabel?: string
    scrollLeftLabel?: string
    scrollRightLabel?: string
  }>(),
  {
    tags: () => [],
    activeKey: '',
    showReload: true,
    reloadLabel: '刷新',
    closeLabel: '关闭标签',
    closeLeftLabel: '关闭左侧',
    closeRightLabel: '关闭右侧',
    closeOthersLabel: '关闭其他',
    closeAllLabel: '关闭全部',
    allTagsLabel: '全部标签',
    scrollLeftLabel: '向左滚动',
    scrollRightLabel: '向右滚动',
  }
)

const emit = defineEmits<{
  select: [tag: AppTag]
  close: [tag: AppTag]
  reload: []
  command: [command: AppTagsCommand, tag: AppTag]
}>()

const ns = useNamespace('app-tags-bar')
const scrollbarRef = ref<{ wrapRef?: HTMLElement }>()
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

let boundWrap: HTMLElement | undefined

const activeIndex = computed(() =>
  props.tags.findIndex((tag) => tag.key === props.activeKey)
)
const canCloseAll = computed(() => hasClosableTags(props.tags))

function canCloseLeft(tag: AppTag) {
  const index = props.tags.findIndex((item) => item.key === tag.key)
  return canCloseTagsLeft(props.tags, index)
}

function canCloseRight(tag: AppTag) {
  const index = props.tags.findIndex((item) => item.key === tag.key)
  return canCloseTagsRight(props.tags, index)
}

function canCloseOthers(tag: AppTag) {
  return canCloseOtherTags(props.tags, tag.key)
}

function onDropdownCommand(command: string | number | object, tag: AppTag) {
  emit('command', command as AppTagsCommand, tag)
}

function handleSelectFromList(key: string) {
  const tag = props.tags.find((item) => item.key === key)
  if (tag) emit('select', tag)
}

function getWrap() {
  return scrollbarRef.value?.wrapRef
}

function updateScrollState() {
  const wrap = getWrap()
  if (!wrap) {
    canScrollLeft.value = false
    canScrollRight.value = false
    return
  }
  canScrollLeft.value = wrap.scrollLeft > 1
  canScrollRight.value =
    wrap.scrollLeft + wrap.clientWidth < wrap.scrollWidth - 1
}

function bindWrapScroll() {
  const wrap = getWrap()
  if (wrap === boundWrap) return
  boundWrap?.removeEventListener('scroll', updateScrollState)
  boundWrap = wrap
  wrap?.addEventListener('scroll', updateScrollState, { passive: true })
  updateScrollState()
}

function scrollTags(direction: -1 | 1) {
  const wrap = getWrap()
  if (!wrap) return
  const distance = Math.max(Math.round(wrap.clientWidth * 0.7), 160)
  wrap.scrollBy({ left: direction * distance, behavior: 'smooth' })
}

function onWheel(event: WheelEvent) {
  const wrap = getWrap()
  if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return
  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
  if (delta === 0) return
  event.preventDefault()
  wrap.scrollLeft += delta
  updateScrollState()
}

function onAuxClick(tag: AppTag, event: MouseEvent) {
  if (event.button !== 1) return
  event.preventDefault()
  if (!tag.affix) emit('close', tag)
}

function onItemMouseDown(event: MouseEvent) {
  if (event.button === 1) event.preventDefault()
}

async function moveToCurrentTag() {
  await nextTick()
  bindWrapScroll()
  const wrap = getWrap()
  if (!wrap) return
  const tags = wrap.getElementsByClassName(ns.e('item'))
  const index = activeIndex.value
  if (index < 0 || !tags.length) return
  const current = tags[index] as HTMLElement | undefined
  if (!current) return
  const left = current.offsetLeft
  const right = left + current.offsetWidth
  if (right > wrap.scrollLeft + wrap.clientWidth) {
    wrap.scrollLeft = right - wrap.clientWidth
  } else if (left < wrap.scrollLeft) {
    wrap.scrollLeft = left
  }
  updateScrollState()
}

watch(
  () => [props.activeKey, props.tags.length] as const,
  () => {
    void moveToCurrentTag()
  }
)

onMounted(() => {
  void moveToCurrentTag()
  window.addEventListener('resize', updateScrollState)
})

onBeforeUnmount(() => {
  boundWrap?.removeEventListener('scroll', updateScrollState)
  window.removeEventListener('resize', updateScrollState)
})
</script>
